import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')
  const leftPanel = getParam(params, 'left_glass_panel')
  const topDoorPanel = getParam(params, 'top_door_glass_panel')
  const doorPanel1 = getParam(params, 'door_glass_panel_1')
  const doorPanel2 = getParam(params, 'door_glass_panel_2')
  const rightPanel = getParam(params, 'right_glass_panel')

  if (!leftFrame || !topFrame) return

  let currentX = leftFrame.width || 0

  if (leftPanel) {
    updateParam(model, 'left_glass_panel', {
      position: { ...leftPanel.position, x: leftFrame.width, y: frameThickness } as ModelParameter['position'],
    })
    currentX += leftPanel.width || 0
  }

  if (topDoorPanel) {
    updateParam(model, 'top_door_glass_panel', {
      position: { ...topDoorPanel.position, x: currentX, y: frameThickness } as ModelParameter['position'],
    })
  }

  if (doorPanel1 && topDoorPanel) {
    const transomHeight = (topFrame.height || 0) + (topDoorPanel.height || 0)
    updateParam(model, 'door_glass_panel_1', {
      position: { x: currentX, y: transomHeight, z: doorPanel1.position?.z || 0 } as ModelParameter['position'],
    })
  }

  if (doorPanel2 && doorPanel1) {
    const door2X = currentX + (doorPanel1.width || 0)
    const transomHeight = (topFrame.height || 0) + (topDoorPanel?.height || 0)
    updateParam(model, 'door_glass_panel_2', {
      position: { x: door2X, y: transomHeight, z: doorPanel2.position?.z || 0 } as ModelParameter['position'],
    })
    currentX = door2X + (doorPanel2.width || 0)
  }

  if (rightPanel) {
    updateParam(model, 'right_glass_panel', {
      position: { ...rightPanel.position, x: currentX, y: frameThickness } as ModelParameter['position'],
    })
  }
}

function updateDoorHardware(model: ModelData, doorId: string, newHeight: number) {
  const doorPanel = getParam(model.parameters!, doorId)
  if (!doorPanel?.hardware) return

  const updatedHardware = { ...doorPanel.hardware }

  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => ({
      ...handle, position: { ...handle.position, y: Math.round(newHeight * 0.47) },
    }))
  }
  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) return { ...hinge, position: { ...hinge.position, y: -glassHingeHeight / 2 } }
      return { ...hinge, position: { ...hinge.position, y: newHeight - glassHingeHeight } }
    })
  }
  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => ({
      ...lock, position: { ...lock.position, y: newHeight - glassLockHeight },
    }))
  }

  updateParam(model, doorId, { hardware: updatedHardware })
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  const currentTotalWidth = model.width as number
  const widthDifference = numValue - currentTotalWidth
  const halfDifference = widthDifference / 2

  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (!cascadeUpdates) return model

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const leftPanel = getParam(model.parameters!, 'left_glass_panel')
  const rightPanel = getParam(model.parameters!, 'right_glass_panel')

  const totalDoorWidth = (doorPanel1?.width || 0) + (doorPanel2?.width || 0) || 1620

  if (bottomLeft && leftFrame) {
    const newLeftSectionWidth = (bottomLeft.width || 0) + halfDifference
    updateParam(model, 'bottom_left_frame_profile', { width: newLeftSectionWidth })
    if (leftPanel) {
      updateParam(model, 'left_glass_panel', { width: newLeftSectionWidth - (leftFrame.width || 0) })
    }
  }

  if (bottomRight && rightFrame) {
    const newRightSectionWidth = (bottomRight.width || 0) + halfDifference
    updateParam(model, 'bottom_right_frame_profile', { width: newRightSectionWidth })
    if (rightPanel) {
      updateParam(model, 'right_glass_panel', { width: newRightSectionWidth - (rightFrame.width || 0) })
    }
  }

  if (rightFrame && bottomLeft) {
    const newLeftSectionWidth = (bottomLeft.width || 0) + halfDifference
    const newRightFrameX = newLeftSectionWidth + totalDoorWidth
    updateParam(model, 'right_frame_profile', { position: { ...rightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
    if (bottomRight) {
      updateParam(model, 'bottom_right_frame_profile', {
        position: { ...bottomRight.position, x: newRightFrameX } as ModelParameter['position'],
      })
    }
  }

  recalculatePositions(model)

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options

  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  if (!cascadeUpdates) return model

  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  if (topDoorPanel && topFrame) {
    const remainingDoorHeight = numValue - (topDoorPanel.height || 0) - (topFrame.height || 0)

    if (leftFrame) {
      updateParam(model, 'left_glass_panel', { height: (leftFrame.height || 0) - frameThickness - frameThickness })
    }
    if (rightFrame) {
      updateParam(model, 'right_glass_panel', { height: (rightFrame.height || 0) - frameThickness - frameThickness })
    }

    updateParam(model, 'door_glass_panel_1', { height: remainingDoorHeight })
    updateParam(model, 'door_glass_panel_2', { height: remainingDoorHeight })

    updateDoorHardware(model, 'door_glass_panel_1', remainingDoorHeight)
    updateDoorHardware(model, 'door_glass_panel_2', remainingDoorHeight)

    const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
    const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
    if (bottomLeft) {
      updateParam(model, 'bottom_left_frame_profile', { position: { ...bottomLeft.position, y: numValue - frameThickness } as ModelParameter['position'] })
    }
    if (bottomRight) {
      updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, y: numValue - frameThickness } as ModelParameter['position'] })
    }
  }

  return model
}

const topCenterHeight: DimensionHandler = (model, _initial, numValue) => {
  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!topDoorPanel || !doorPanel1 || !doorPanel2 || !topFrame) return model

  const newTopDoorHeight = numValue - (topFrame.height || 0)
  updateParam(model, 'top_door_glass_panel', { height: newTopDoorHeight, position: { ...topDoorPanel.position, y: frameThickness } as ModelParameter['position'] })

  updateParam(model, 'door_glass_panel_1', { position: { ...doorPanel1.position, y: numValue } as ModelParameter['position'] })
  updateParam(model, 'door_glass_panel_2', { position: { ...doorPanel2.position, y: numValue } as ModelParameter['position'] })

  const newTotalHeight = numValue + (doorPanel1.height || 0)
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) updateParam(model, 'left_glass_panel', { height: (leftFrame.height || 0) - frameThickness - frameThickness })
  if (rightFrame) updateParam(model, 'right_glass_panel', { height: (rightFrame.height || 0) - frameThickness - frameThickness })

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) updateParam(model, 'bottom_left_frame_profile', { position: { ...bottomLeft.position, y: newTotalHeight - frameThickness } as ModelParameter['position'] })
  if (bottomRight) updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, y: newTotalHeight - frameThickness } as ModelParameter['position'] })

  return model
}

const centerPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const topDoorPanel = getParam(model.parameters!, 'top_door_glass_panel')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')

  if (!doorPanel1 || !doorPanel2 || !topDoorPanel || !topFrame) return model

  updateParam(model, 'door_glass_panel_1', { height: numValue })
  updateParam(model, 'door_glass_panel_2', { height: numValue })

  const newTotalHeight = (topFrame.height || 0) + (topDoorPanel.height || 0) + numValue
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (leftFrame) updateParam(model, 'left_glass_panel', { height: (leftFrame.height || 0) - frameThickness - frameThickness })
  if (rightFrame) updateParam(model, 'right_glass_panel', { height: (rightFrame.height || 0) - frameThickness - frameThickness })

  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  if (bottomLeft) updateParam(model, 'bottom_left_frame_profile', { position: { ...bottomLeft.position, y: newTotalHeight - frameThickness } as ModelParameter['position'] })
  if (bottomRight) updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, y: newTotalHeight - frameThickness } as ModelParameter['position'] })

  updateDoorHardware(model, 'door_glass_panel_1', numValue)
  updateDoorHardware(model, 'door_glass_panel_2', numValue)

  return model
}

const parameterWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { positionId, cascadeUpdates = true } = options as { positionId?: string; cascadeUpdates?: boolean }
  if (!positionId) return model

  const currentParameter = getParam(model.parameters!, positionId)
  if (!currentParameter) return model

  const currentWidth = currentParameter.width || 0
  const widthDifference = numValue - currentWidth

  updateParam(model, positionId, { width: numValue })

  if (!cascadeUpdates) return model

  if (positionId === 'door_glass_panel_1') {
    const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
    const newTotalDoorWidth = numValue + (doorPanel2?.width || 0)
    updateParam(model, 'top_door_glass_panel', { width: newTotalDoorWidth })
    model.width = (model.width as number) + widthDifference
    updateParam(model, 'top_frame_profile', { width: model.width as number })

    if (currentParameter.hardware) {
      const updatedHardware = { ...currentParameter.hardware }
      if (updatedHardware.handles) {
        updatedHardware.handles = updatedHardware.handles.map((handle) => ({
          ...handle, position: { ...handle.position, x: numValue * (handle.position.x / currentWidth) },
        }))
      }
      if (updatedHardware.locks) {
        updatedHardware.locks = updatedHardware.locks.map((lock) => ({
          ...lock, position: { ...lock.position, x: numValue - glassLockWidth * 1.5 },
        }))
      }
      updateParam(model, positionId, { hardware: updatedHardware })
    }
  } else if (positionId === 'door_glass_panel_2') {
    const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
    const newTotalDoorWidth = (doorPanel1?.width || 0) + numValue
    updateParam(model, 'top_door_glass_panel', { width: newTotalDoorWidth })
    model.width = (model.width as number) + widthDifference
    updateParam(model, 'top_frame_profile', { width: model.width as number })

    if (currentParameter.hardware) {
      const updatedHardware = { ...currentParameter.hardware }
      if (updatedHardware.hinges) {
        updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
          if (hinge.positionId === 'door_2_right_top_hinge_1') {
            return { ...hinge, position: { ...hinge.position, x: numValue - glassHingeWidth / 2 } }
          } else if (hinge.positionId === 'door_2_right_bottom_hinge_2') {
            return { ...hinge, position: { ...hinge.position, x: numValue - glassHingeWidth } }
          }
          return hinge
        })
      }
      if (updatedHardware.handles) {
        updatedHardware.handles = updatedHardware.handles.map((handle) => ({
          ...handle, position: { ...handle.position, x: numValue * (handle.position.x / currentWidth) },
        }))
      }
      if (updatedHardware.locks) {
        updatedHardware.locks = updatedHardware.locks.map((lock) => ({
          ...lock, position: { ...lock.position, x: glassLockWidth },
        }))
      }
      updateParam(model, positionId, { hardware: updatedHardware })
    }
  }

  recalculatePositions(model)

  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomLeft = getParam(model.parameters!, 'bottom_left_frame_profile')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')

  if (!bottomLeft || !leftFrame) return model

  const widthDifference = numValue - (bottomLeft.width || 0)
  updateParam(model, 'bottom_left_frame_profile', { width: numValue })
  updateParam(model, 'left_glass_panel', { width: numValue - (leftFrame.width || 0) })
  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  recalculatePositions(model)

  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const bottomRight = getParam(model.parameters!, 'bottom_right_frame_profile')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')

  if (!bottomRight || !rightFrame) return model

  const widthDifference = numValue - (bottomRight.width || 0)
  updateParam(model, 'bottom_right_frame_profile', { width: numValue })
  updateParam(model, 'right_glass_panel', { width: numValue - (rightFrame.width || 0) })
  model.width = (model.width as number) + widthDifference
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const leftSectionWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 390
  const doorPanel1 = getParam(model.parameters!, 'door_glass_panel_1')
  const doorPanel2 = getParam(model.parameters!, 'door_glass_panel_2')
  const totalDoorWidth = (doorPanel1?.width || 810) + (doorPanel2?.width || 810)
  const newRightFrameX = leftSectionWidth + totalDoorWidth

  updateParam(model, 'right_frame_profile', { position: { ...rightFrame.position, x: newRightFrameX } as ModelParameter['position'] })
  updateParam(model, 'bottom_right_frame_profile', { position: { ...bottomRight.position, x: newRightFrameX } as ModelParameter['position'] })

  recalculatePositions(model)

  return model
}

export const model2_2DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  topCenterHeight,
  centerPanelHeight,
  parameterWidth,
  leftFrameWidth,
  rightFrameWidth,
}
